import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { mergeMap, map, catchError, switchMap } from 'rxjs/operators';
import { of, forkJoin } from 'rxjs';
import {
  LoadOrganizationUsers,
  SetOrganizationUsers,
  SetOrganizationUserErrored,
  UpdateOrganizationUser,
  ShowDisableOrganizationUserConfirm,
  RemoveOrganizationUserLegacy,
  RemoveOrganizationUserCancelled,
  RemoveOrganizationUserErrored,
  AddOrganizationUser,
  AddOrganizationUserSucceeded,
  AddOrganizationUserErrored,
  UpdateOrganizationUserSucceeded,
  UpdateOrganizationUserErrored,
  RemoveOrganizationUserSucceeded,
  RemoveOrganizationUser,
} from './organization-user.actions';
import { OrganizationUserService } from './organization-user.service';

@Injectable()
export class OrganizationUserEffects {
  loadOrganizationUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LoadOrganizationUsers,
        AddOrganizationUserSucceeded,
        UpdateOrganizationUserSucceeded,
        RemoveOrganizationUserSucceeded,
      ),
      switchMap(({ organizationId }) =>
        this.organizationUserService.loadOrganizationUsers(organizationId).pipe(
          map(({ users }) => SetOrganizationUsers({ users })),
          catchError((error) => of(SetOrganizationUserErrored({ error }))),
        ),
      ),
    ),
  );

  addOrganizationUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddOrganizationUser),
      mergeMap(({ user, organizationId }) =>
        this.organizationUserService.addOrganizationUser(user).pipe(
          map(() => AddOrganizationUserSucceeded({ organizationId })),
          catchError((error) => of(AddOrganizationUserErrored({ error }))),
        ),
      ),
    ),
  );

  updateOrganizationUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateOrganizationUser),
      mergeMap(({ userId, user, organizationId }) =>
        this.organizationUserService.updateOrganizationUser(userId, user).pipe(
          map(() => UpdateOrganizationUserSucceeded({ organizationId })),
          catchError((error) => of(UpdateOrganizationUserErrored({ error }))),
        ),
      ),
    ),
  );

  showDisableOrganizationUserConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShowDisableOrganizationUserConfirm),
      mergeMap((action) =>
        forkJoin([
          of(action),
          this.organizationUserService.showDisableOrganizationUserConfirm(action.user),
        ]),
      ),
      map(([action, result]) => {
        if (result) {
          return RemoveOrganizationUser({
            userId: action.user.user_id,
            organizationId: action.organizationId,
          });
        }
        return RemoveOrganizationUserCancelled();
      }),
      catchError((error) => of(SetOrganizationUserErrored({ error }))),
    ),
  );
  // TODO - retire legacy code STUD-2700
  removeOrganizationUserLegacy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveOrganizationUserLegacy),
      mergeMap(({ userId, organizationId }) =>
        this.organizationUserService.removeOrganizationUserLegacy(userId, organizationId).pipe(
          map(() => RemoveOrganizationUserSucceeded({ organizationId })),
          catchError((error) => of(RemoveOrganizationUserErrored({ error }))),
        ),
      ),
    ),
  );

  removeOrganizationUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveOrganizationUser),
      mergeMap(({ userId, organizationId }) =>
        this.organizationUserService.removeOrganizationUser(userId, organizationId).pipe(
          map(() => RemoveOrganizationUserSucceeded({ organizationId })),
          catchError((error) => of(RemoveOrganizationUserErrored({ error }))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private organizationUserService: OrganizationUserService,
  ) {}
}
